import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { apiFetch } from '../../lib/api.js'
import startConfigImg from '../../assets/start-config.png'
import DeckCard from '../ui/DeckCard.jsx'
import DuplicateOverlay from '../ui/DuplicateOverlay.jsx'

const EDUCATION_LEVELS = [
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',
  '6ème', '5ème', '4ème', '3ème', 'CAP',
  '2nde', '1ère', 'Terminale',
  'BTS/BUT', 'Licence', 'Master',
  'Formation continue', 'Non applicable',
]

// noLocationSnackbar : passer true quand le parent gère déjà le snackbar de location.state (ex. HostDecksPage)
export default function DeckManager({ myDecksOnly = false, communityOnly = false, darkMode = false, defaultCreateOpen = false, navBase = '/admin/decks', noLocationSnackbar = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [decks, setDecks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [snackbar, setSnackbar] = useState(!noLocationSnackbar ? (location.state?.snackbar ?? '') : '')
  const [duplicatePhase, setDuplicatePhase] = useState(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLevels, setSelectedLevels] = useState(new Set())
  const [selectedMatieres, setSelectedMatieres] = useState(new Set())

  useEffect(() => {
    if (snackbar) {
      const t = setTimeout(() => setSnackbar(''), 4000)
      return () => clearTimeout(t)
    }
  }, [snackbar])

  async function fetchAll() {
    setLoading(true)
    try {
      const deckUrl = communityOnly ? '/api/decks?tab=community' : myDecksOnly ? '/api/decks?tab=mine' : '/api/decks'
      const deckData = await apiFetch(deckUrl)
      setDecks(deckData)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  useEffect(() => { fetchAll() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (defaultCreateOpen) navigate(`${navBase}/new`) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const availableLevels = EDUCATION_LEVELS.filter(lvl =>
    lvl !== 'Non applicable' && decks.some(d => d.education_level === lvl)
  )

  const availableMatieres = [...new Set(
    decks.map(d => d.matiere).filter(Boolean)
  )].sort()

  const filteredDecks = decks.filter(deck => {
    if (selectedLevels.size > 0 && !selectedLevels.has(deck.education_level)) return false
    if (selectedMatieres.size > 0 && !selectedMatieres.has(deck.matiere)) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return deck.titre.toLowerCase().includes(q)
          || deck.keywords?.some(kw => kw.toLowerCase().includes(q))
          || deck.matiere?.toLowerCase().includes(q)
    }
    return true
  })

  const hasActiveFilters = searchQuery.trim() !== '' || selectedLevels.size > 0 || selectedMatieres.size > 0

  function toggleLevel(lvl) {
    setSelectedLevels(prev => {
      const next = new Set(prev)
      next.has(lvl) ? next.delete(lvl) : next.add(lvl)
      return next
    })
  }

  function toggleMatiere(m) {
    setSelectedMatieres(prev => {
      const next = new Set(prev)
      next.has(m) ? next.delete(m) : next.add(m)
      return next
    })
  }

  function clearFilters() {
    setSearchQuery('')
    setSelectedLevels(new Set())
    setSelectedMatieres(new Set())
  }

  async function handleDelete(deck) {
    if (!confirm(`Supprimer le quiz "${deck.titre}" et toutes ses questions ?`)) return
    setError('')
    try {
      await apiFetch(`/api/decks/${deck.id}`, { method: 'DELETE' })
      setDecks(ds => ds.filter(d => d.id !== deck.id))
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleDuplicate(deck) {
    setError('')
    setDuplicatePhase('loading')
    try {
      const { id: newId } = await apiFetch(`/api/decks/${deck.id}/duplicate`, { method: 'POST' })
      setDuplicatePhase('done')
      const destBase = navBase.startsWith('/admin') ? '/admin/decks' : '/host/decks'
      setTimeout(() => {
        navigate(`${destBase}/${newId}/questions`, {
          state: { snackbar: 'Quiz dupliqué — vous êtes maintenant en train d\'éditer votre copie' },
        })
      }, 1200)
    } catch (e) {
      setDuplicatePhase(null)
      setError(e.message)
    }
  }

  return (
    <div>
      <DuplicateOverlay phase={duplicatePhase} />
      {snackbar && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium">
          ✅ {snackbar}
        </div>
      )}
      {!communityOnly && (
        <div className={`flex items-center mb-6 ${darkMode ? 'justify-end' : 'justify-between'}`}>
          {!darkMode && <h1 className="text-2xl font-semibold text-white">Quiz</h1>}
          <button
            onClick={() => navigate(`${navBase}/new`)}
            className="btn-primary px-4 py-2"
          >
            + Nouveau quiz
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-2 mb-4">{error}</div>
      )}

      {!loading && decks.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher un quiz…"
              className={`w-full rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                darkMode
                  ? 'bg-gray-800 border border-white/20 text-white placeholder-white/40'
                  : 'bg-white/10 border border-white/20 text-white placeholder-white/40'
              }`}
            />
          </div>

          {availableLevels.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {availableLevels.map(lvl => (
                <button
                  key={lvl}
                  onClick={() => toggleLevel(lvl)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    selectedLevels.has(lvl)
                      ? 'bg-primary text-white border-primary'
                      : darkMode
                    ? 'bg-gray-800 border-white/20 text-white/80 hover:bg-gray-700 hover:text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          )}

          {availableMatieres.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {availableMatieres.map(m => (
                <button
                  key={m}
                  onClick={() => toggleMatiere(m)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    selectedMatieres.has(m)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : darkMode
                    ? 'bg-gray-800 border-white/20 text-white/80 hover:bg-gray-700 hover:text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {m}
                </button>
              ))}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs px-3 py-1 rounded-full border border-red-400/50 text-red-400 hover:bg-red-500/20 transition"
                >
                  ✕ Tout effacer
                </button>
              )}
            </div>
          )}

          {availableMatieres.length === 0 && hasActiveFilters && (
            <div className="flex gap-2">
              <button
                onClick={clearFilters}
                className="text-xs px-3 py-1 rounded-full border border-red-400/50 text-red-400 hover:bg-red-500/20 transition"
              >
                ✕ Tout effacer
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-white/40">Chargement…</div>
      ) : decks.length === 0 ? (
        communityOnly ? (
          <div className="text-center py-12 text-white/40">
            Aucun quiz communautaire disponible pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => navigate(`${navBase}/new`)}
              className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200 text-left"
            >
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 p-4 flex items-center gap-4 h-full">
                <img src={startConfigImg} alt="" className="w-16 h-16 object-contain shrink-0" />
                <p className="text-white font-medium text-sm leading-snug">
                  Crée ton premier quiz en quelques clics !
                </p>
              </div>
            </button>
          </div>
        )
      ) : filteredDecks.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          Aucun quiz ne correspond aux filtres.{' '}
          <button onClick={clearFilters} className="text-primary hover:underline">Tout effacer</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDecks.map(deck => (
            <DeckCard
              key={deck.id}
              deck={deck}
              transparent={darkMode}
              readOnly={communityOnly}
              onViewQuiz={() => navigate(`${navBase}/${deck.id}/questions`)}
              onManageThemes={communityOnly ? undefined : () => navigate(`${navBase}/${deck.id}`, { state: { returnTo: location.pathname } })}
              onEdit={communityOnly ? undefined : () => navigate(`${navBase}/${deck.id}/edit`, { state: { returnTo: location.pathname } })}
              onDelete={communityOnly ? undefined : () => handleDelete(deck)}
              onDuplicate={() => handleDuplicate(deck)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
